import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';

// Fail fast if environment variable is missing - no silent fallback to wrong environment
const API_BASE_URL = import.meta.env.VITE_API_URL;
if (!API_BASE_URL) {
  throw new Error('VITE_API_URL environment variable is required. Check your .env file.');
}

// Global reference to auth functions (will be set by AuthProvider)
let globalRefreshFunction: (() => Promise<void>) | null = null;
let globalLogoutFunction: (() => void) | null = null;
let globalViewAsRole: string | null = null;
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

export const setGlobalRefreshFunction = (refreshFn: () => Promise<void>) => {
  globalRefreshFunction = refreshFn;
};

export const setGlobalLogoutFunction = (logoutFn: () => void) => {
  globalLogoutFunction = logoutFn;
};

export const setGlobalViewAsRole = (role: string | null) => {
  globalViewAsRole = role;
};

export const getGlobalViewAsRole = (): string | null => {
  return globalViewAsRole;
};

/**
 * Call this from anywhere to trigger centralized logout
 * Used when auth refresh fails completely
 */
export const triggerGlobalLogout = () => {
  if (globalLogoutFunction) {
    globalLogoutFunction();
  }
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

export const createAuthenticatedApiClient = (idToken: string): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  });

  // Request interceptor for view-as header
  client.interceptors.request.use((config) => {
    if (globalViewAsRole) {
      config.headers['X-OpsPilot-View-As'] = globalViewAsRole;
    }
    return config;
  });

  // Response interceptor for handling 401/403
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // 403 = forbidden — reject immediately, no token refresh
      if (error.response?.status === 403) {
        return Promise.reject(error);
      }

      // 401 = expired token — attempt refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // Wait for ongoing refresh to complete
          return new Promise((resolve) => {
            addRefreshSubscriber((token: string) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              resolve(axios(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          if (globalRefreshFunction) {
            await globalRefreshFunction();

            // Get the new token from sessionStorage
            const newToken = sessionStorage.getItem('cognito_id_token');
            if (newToken) {
              isRefreshing = false;
              onRefreshed(newToken);

              // Retry original request with new token
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            } else {
              // No new token available after refresh - logout
              isRefreshing = false;
              triggerGlobalLogout();
              return Promise.reject(new Error('Session expired - please log in again'));
            }
          } else {
            // No refresh function available - logout
            isRefreshing = false;
            triggerGlobalLogout();
            return Promise.reject(new Error('Session expired - please log in again'));
          }
        } catch (refreshError) {
          isRefreshing = false;
          // Refresh failed - trigger centralized logout
          triggerGlobalLogout();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

export default createAuthenticatedApiClient;
